import { useCallback, useMemo } from 'react';
import { useReadResource } from '@/hooks/useReadResource';
import ReadNotice from '@/components/feature/ReadNotice';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { forexApi, toBrokerAccount, toFollowRecord, toUiAgentId } from '@/services/forexApi';
import { accountStatusConfig } from '@/lib/brokerAccounts';

export default function MT4StatusCard() {
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const identity = accessToken || '';
  const accountsRead = useReadResource(identity, useCallback(() => forexApi.listFollowerAccounts(accessToken), [accessToken]), !!accessToken);
  const followsRead = useReadResource(identity, useCallback(() => forexApi.listFollowRelations(accessToken), [accessToken]), !!accessToken);
  const agentsRead = useReadResource(identity, useCallback(() => forexApi.listPlatformAgents(accessToken), [accessToken]), !!accessToken);
  const accounts = accountsRead.data?.map(toBrokerAccount) || [];
  const state = { accounts, follows: followsRead.data?.map(relation => toFollowRecord(relation, accounts)) || [], agents: agentsRead.data || [], loading: accountsRead.loading && !accountsRead.data };
  const notices = <div className="px-5 mt-3 space-y-2">
    <ReadNotice {...accountsRead} label="账户状态" onRetry={accountsRead.refresh} />
    <ReadNotice {...followsRead} label="跟随状态" onRetry={followsRead.refresh} />
    <ReadNotice {...agentsRead} label="策略名称" onRetry={agentsRead.refresh} />
  </div>;

  const agentNameById = useMemo(() => {
    return state.agents.reduce<Record<string, string>>((acc, agent) => {
      acc[String(toUiAgentId(agent.agent_id))] = agent.name;
      return acc;
    }, {});
  }, [state.agents]);

  if (state.loading) {
    return (
      <div className="px-5 mt-4">
        {notices}
        <div className="bg-white rounded-2xl h-[70px] animate-pulse" />
      </div>
    );
  }

  if (!accountsRead.data || (accountsRead.error && state.accounts.length === 0)) return notices;

  if (state.accounts.length === 0) {
    return (
      <div className="px-5 mt-4">
        {notices}
        <button
          onClick={() => navigate('/broker')}
          className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl active:scale-[0.99] transition-transform"
          style={{
            background: '#FFFBEB',
            border: '1px solid #FDE68A',
          }}
        >
          <div className="w-9 h-9 flex items-center justify-center rounded-xl flex-shrink-0" style={{ background: '#FEF3C7' }}>
            <i className="ri-link-m text-base" style={{ color: '#D97706' }} />
          </div>
          <div className="flex-1 text-left">
            <p className="text-sm font-semibold" style={{ color: '#92400E' }}>
              绑定交易账户
            </p>
            <p className="text-[11px] mt-0.5" style={{ color: '#B45309' }}>
              连接后需授权策略并查看实际执行就绪状态
            </p>
          </div>
          <i className="ri-arrow-right-s-line flex-shrink-0" style={{ color: '#D97706' }} />
        </button>
      </div>
    );
  }

  const connected = state.accounts.find((account) => account.accountStatus === 'connected');
  const reviewing = state.accounts.find((account) => account.accountStatus === 'reviewing');
  const displayAccount = connected || reviewing || state.accounts[0];
  const cfg = accountStatusConfig[displayAccount.accountStatus];
  const activeFollow = connected
    ? state.follows.find((follow) => follow.accountId === displayAccount.id && follow.followStatus === 'running')
    : null;
  const followingAgentName = activeFollow
    ? agentNameById[String(activeFollow.agentId)] || '策略名称暂不可用'
    : null;

  if (connected && activeFollow && followingAgentName) {
    return (
      <div className="px-5 mt-4">
        {notices}
        <div
          className="bg-white rounded-2xl px-4 py-3.5"
          style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.04)', border: '1px solid rgba(0,0,0,0.03)' }}
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0" style={{ background: '#EEF2FF' }}>
              <i className="ri-robot-2-fill text-lg" style={{ color: '#6366F1' }} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-gray-900 font-semibold text-sm">跟随 {followingAgentName}</p>
              <p className="text-gray-500 text-xs mt-0.5">
                {displayAccount.brokerName} · ····{displayAccount.accountNo.slice(-4)} · 跟单中
              </p>
            </div>
            <button
              onClick={() => navigate(`/copy-config?agentId=${activeFollow.agentId}`)}
              className="flex items-center gap-1 text-xs font-medium flex-shrink-0"
              style={{ color: '#6366F1' }}
            >
              管理 <i className="ri-arrow-right-s-line" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="px-5 mt-4">
        {notices}
      <div
        className="bg-white rounded-2xl px-4 py-3.5"
        style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.04)', border: '1px solid rgba(0,0,0,0.03)' }}
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0" style={{ background: '#F0FDF4' }}>
            <i className="ri-link-m text-lg" style={{ color: '#10B981' }} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-gray-900 font-semibold text-sm">交易账户</p>
            <p className="text-gray-500 text-xs mt-0.5">
              {displayAccount.brokerName} · ····{displayAccount.accountNo.slice(-4)} · {cfg.label} · {followsRead.error ? '跟随状态暂不可用' : followsRead.loading ? '跟随状态读取中' : '以当前跟随状态为准'}
            </p>
          </div>
          <button
            onClick={() => navigate('/broker')}
            className="flex items-center gap-1 text-xs font-medium flex-shrink-0"
            style={{ color: '#6366F1' }}
          >
            管理 <i className="ri-arrow-right-s-line" />
          </button>
        </div>
      </div>
    </div>
  );
}
